import { z } from 'zod';

/**
 * Where one detected violation stands — **state machine 3 of
 * `00-full-system.md`**, transcribed rather than re-derived.
 *
 * - `detected` — the classifier wrote it. Transient by design: the same job that
 *   creates the row moves it to `enforced` or `queued_for_review` before it
 *   commits anything else. A row left here is a job that died, which is exactly
 *   what makes it worth being a state rather than an implicit nothing.
 * - `queued_for_review` — a human must decide. Reached three ways, all of them
 *   fail-closed (N15): the confidence was below `moderation.ai.confidenceThreshold`,
 *   the AI refused or the budget was exhausted, or the type is configured to be
 *   reviewed before it is acted on.
 * - `enforced` — the action ran. This is the only state a member can appeal.
 * - `dismissed` — a moderator looked and found nothing. Terminal.
 * - `appealed` — the member has contested an enforcement.
 * - `upheld` / `overturned` — the appeal's verdict. Terminal. An overturn
 *   reverses the action where the action holds a state
 *   (`isReversibleViolationAction`).
 */
export const VIOLATION_STATUSES = [
  'detected',
  'queued_for_review',
  'enforced',
  'dismissed',
  'appealed',
  'upheld',
  'overturned',
] as const;

export const ViolationStatusSchema = z.enum(VIOLATION_STATUSES);

export type ViolationStatus = (typeof VIOLATION_STATUSES)[number];

/** What the classifier writes before it decides anything. */
export const INITIAL_VIOLATION_STATUS: ViolationStatus = 'detected';

/**
 * The edges of state machine 3, and **only** those.
 *
 * Written as a table rather than as a chain of `if`s so the diagram and the code
 * can be read against each other in one glance — the discipline
 * `USER_STATUS_TRANSITIONS` keeps. Two absences are load-bearing and are
 * asserted in the spec: `detected → dismissed` (a dismissal is a person's
 * verdict, and a person reaches it through the queue) and
 * `queued_for_review → appealed` (nothing is appealed before there is a
 * consequence to appeal against).
 */
const VIOLATION_TRANSITIONS: Readonly<Record<ViolationStatus, readonly ViolationStatus[]>> = {
  detected: ['enforced', 'queued_for_review'],
  queued_for_review: ['enforced', 'dismissed'],
  enforced: ['appealed'],
  appealed: ['upheld', 'overturned'],
  dismissed: [],
  upheld: [],
  overturned: [],
};

export function canTransitionViolation(from: ViolationStatus, to: ViolationStatus): boolean {
  return VIOLATION_TRANSITIONS[from].includes(to);
}

/**
 * Is somebody waiting on a moderator?
 *
 * The predicate the queue's `WHERE` is built from, so "what is open" has one
 * spelling — the discipline `isOpenReportStatus` keeps for reports, and for the
 * same reason: a status counted open in one place and omitted in another is a
 * case that silently never reaches anybody.
 *
 * `detected` is **not** open: it is the transient state inside the classifying
 * job, and listing it would show reviewers rows that are about to move on their
 * own.
 */
export function isOpenViolationStatus(status: ViolationStatus): boolean {
  return status === 'queued_for_review' || status === 'appealed';
}

/** Nothing more will happen to this violation. */
export function isTerminalViolationStatus(status: ViolationStatus): boolean {
  return VIOLATION_TRANSITIONS[status].length === 0;
}

/**
 * May the member contest this?
 *
 * Only an enforcement, because only an enforcement did something to them. A
 * queued detection has had no effect yet, and a dismissal already went their
 * way. The endpoint refuses anything else rather than recording an appeal
 * against nothing (N20's "appealable" is about consequences).
 */
export function isAppealableViolationStatus(status: ViolationStatus): boolean {
  return status === 'enforced';
}

/**
 * Does this violation count as a strike when a reviewer reads the member's
 * history — and when `EnforcementService` asks whether a heavier action already
 * stands?
 *
 * An **overturned** violation does not, and that is the whole point of an
 * appeal: a verdict that was withdrawn must stop being evidence, or "overturned"
 * is a label with no consequence. A `dismissed` one never was evidence. A
 * violation under appeal still counts while the appeal is undecided — the
 * consequence is standing during that window, and a member could otherwise park
 * an appeal to neutralise their record.
 */
export function countsAgainstMember(status: ViolationStatus): boolean {
  return status === 'enforced' || status === 'appealed' || status === 'upheld';
}
